/**
 * Synthèse vocale depuis un geste utilisateur (clic / touche).
 * - Sur Chrome Android, `cancel()` juste avant `speak()` peut annuler la nouvelle phrase.
 * - Ne pas forcer `utterance.voice` ici : une référence de voix peut bloquer toute lecture sans erreur.
 */

const GESTURE_UTTERANCE_RATE = 1.05;
const GESTURE_UTTERANCE_PITCH = 1;

let iosPrimed = false;

export function isSpeechSynthesisAvailable() {
  if (typeof window === 'undefined') return false;
  const s = window.speechSynthesis;
  return Boolean(
    s &&
    typeof s.speak === 'function' &&
    typeof s.getVoices === 'function' &&
    typeof window.SpeechSynthesisUtterance === 'function'
  );
}

export function cancelOngoingSpeechOnly(synth) {
  if (!synth) return;
  try {
    if (synth.speaking || synth.pending) synth.cancel();
  } catch {
    /* ignore */
  }
}

export function primeSpeechSynthesisFromUserInteraction() {
  if (!isSpeechSynthesisAvailable()) return;
  const synth = window.speechSynthesis;
  try {
    synth.resume();
  } catch {
    /* ignore */
  }
  const iOS =
    /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!iOS || iosPrimed) return;
  try {
    const u = new SpeechSynthesisUtterance('\u00A0');
    u.volume = 0;
    synth.speak(u);
    synth.cancel();
  } catch {
    /* ignore */
  }
  iosPrimed = true;
}

export function scheduleSpeechEngagedOrFallback({ onEngaged, onSilent, delayMs = 320 }) {
  return window.setTimeout(() => {
    const s = window.speechSynthesis;
    if (s?.speaking || s?.pending) onEngaged?.();
    else onSilent?.();
  }, delayMs);
}

/**
 * Lance la lecture dans la même pile que l’événement utilisateur.
 * @returns {boolean} true si `speak` a été appelé sans exception
 */
export function speakTextFromUserGesture(text, callbacks) {
  const raw = String(text ?? '').trim();
  if (!raw || !isSpeechSynthesisAvailable()) return false;
  const synth = window.speechSynthesis;
  const cb = callbacks && typeof callbacks === 'object' ? callbacks : null;

  primeSpeechSynthesisFromUserInteraction();
  cancelOngoingSpeechOnly(synth);
  try {
    synth.resume();
  } catch {
    /* ignore */
  }

  try {
    synth.getVoices();
    synth.getVoices();
  } catch {
    /* ignore */
  }

  const u = new SpeechSynthesisUtterance(raw);
  u.lang = 'fr-FR';
  u.rate = GESTURE_UTTERANCE_RATE;
  u.pitch = GESTURE_UTTERANCE_PITCH;
  u.volume = 1;
  if (cb?.onStart) u.onstart = () => cb.onStart();
  if (cb?.onEnd) u.onend = () => cb.onEnd();
  if (cb?.onError) u.onerror = (ev) => cb.onError(ev);

  try {
    synth.speak(u);
    return true;
  } catch {
    return false;
  }
}
